import type {
  ChatMessage,
  MemberEventAction,
  MemberEventMessage,
} from "./types";

const TIME_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** "Mar 2 · 18:04" — message timestamp, mirrors the mockup `.msg-time`. */
export function formatMessageTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  // Intl yields "Mar 2, 18:04"; the mockup uses a middot separator.
  const parts = TIME_FMT.formatToParts(d);
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  const day = parts.find((p) => p.type === "day")?.value ?? "";
  const hour = parts.find((p) => p.type === "hour")?.value ?? "";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "";
  return `${month} ${day} · ${hour}:${minute}`;
}

const MEMBER_EVENT_ACTIONS: ReadonlySet<string> = new Set<MemberEventAction>([
  "created",
  "joined",
  "left",
  "kicked",
  "promoted",
  "demoted",
  "leader_changed",
]);

/**
 * Whether a feed message records a roster change rather than something a player
 * or a moderator said. `event_action` is the server's own discriminator: it is
 * set on exactly those rows and on no others.
 *
 * The membership test is not belt-and-braces. `event_action` is TEXT on the
 * wire and the union in types.ts is a promise TypeScript cannot check, so a
 * seventh action added by a migration would reach a tab still running the old
 * bundle — routine during a deploy — and an icon lookup would come back
 * undefined, which React renders by throwing "Element type is invalid". There
 * is no ErrorBoundary in this app, so that takes the whole guild page down over
 * one row. Falling through to SystemMessage instead shows the English sentence
 * the server put in `body` for exactly this reader.
 */
export function isMemberEvent(msg: ChatMessage): msg is MemberEventMessage {
  return msg.event_action !== undefined && MEMBER_EVENT_ACTIONS.has(msg.event_action);
}
